import { Children, useState, useRef, useLayoutEffect, useEffect } from 'react';
import { Grid } from '@mui/material';
import { TruncateChip } from './TruncateChip';
import { TruncateIcon } from '../../assets';

type ChipColor =
    | 'primary'
    | 'default'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning'
    | 'grayscale';

interface ChipListProps {
    chipData:
        | string[]
        | {
              label: string;
              color: ChipColor;
          }[];
    chipTruncateLength?: number;
    chipVariant?: 'filled' | 'outlined';
    chipColor?: ChipColor;
    isRowTruncate?: boolean;
    isMultiLineChip?: boolean;
    chipStyles?: any;
}

const styles = {
    listContainerStyle: {
        display: 'flex',
        flexWrap: 'nowrap',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        columnGap: '8px',
        width: '100%',
    },
};

export const TruncateChipList = ({
    chipColor = 'primary',
    chipVariant = 'filled',
    chipData,
    chipTruncateLength = 25,
    isRowTruncate = true,
    isMultiLineChip = false,
    chipStyles = {},
}: ChipListProps): JSX.Element => {
    const [visibleChips, setVisibleChips] =
        useState<(string | { label: string; color: ChipColor })[]>(chipData);
    const [showTruncateIcon, setShowTruncateIcon] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (!containerRef?.current) return;

        const truncateChip = (chip: string, maxLength: number): string =>
            chip.length > maxLength ? chip.slice(0, maxLength) + '...' : chip;

        const calculateChipWidth = (index: number): number =>
            containerRef.current?.children[index]?.clientWidth ?? 0;

        const resizeObserver = new ResizeObserver(() => {
            const tempVisibleChips: (
                | string
                | { label: string; color: ChipColor }
            )[] = [];
            let totalWidth = 0;
            const containerWidth =
                (containerRef.current?.offsetWidth ?? 0) - 24 - 25;

            chipData.forEach(
                (
                    chip: string | { label: string; color: ChipColor },
                    index: number,
                    arr
                ) => {
                    let chipLabel =
                        typeof chip === 'string' ? chip : chip.label;

                    if (isRowTruncate) {
                        const chipWidth = calculateChipWidth(index);
                        chipLabel = truncateChip(chipLabel, chipTruncateLength);

                        totalWidth +=
                            chipWidth + (arr.length > 1 && index > 0 ? 8 : 0);

                        if (totalWidth <= containerWidth) {
                            tempVisibleChips.push(
                                typeof chip === 'string'
                                    ? chipLabel
                                    : { label: chipLabel, color: chip.color }
                            );
                            setShowTruncateIcon(false);
                        } else {
                            setShowTruncateIcon(true);
                        }
                    } else {
                        tempVisibleChips.push(chip);
                    }
                }
            );
            setVisibleChips(tempVisibleChips);
        });

        resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (containerRef?.current) {
            const resizeObserver = new ResizeObserver(() => {
                visibleChips.forEach(
                    (
                        _: string | { label: string; color: ChipColor },
                        index: number
                    ) => {
                        const container =
                            containerRef?.current?.children[index];
                        if (container) {
                            const labelElm =
                                container.querySelector('.MuiChip-label');
                            if (labelElm?.innerHTML.includes('...')) {
                                labelElm.innerHTML =
                                    labelElm.innerHTML.substring(
                                        0,
                                        labelElm.innerHTML.lastIndexOf('...')
                                    ) +
                                    `<img src=${TruncateIcon} class="ellipsis-icon" alt="truncate-ellipsis-icon"/>`;
                            }
                        }
                    }
                );
            });

            resizeObserver.observe(containerRef.current);

            return () => {
                resizeObserver.disconnect();
            };
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [containerRef?.current, visibleChips]);

    return (
        <Grid ref={containerRef} sx={styles.listContainerStyle}>
            {Children.toArray(
                visibleChips.map((chipItem) => (
                    <TruncateChip
                        color={
                            typeof chipItem === 'string'
                                ? chipColor
                                : chipItem.color
                        }
                        label={
                            typeof chipItem === 'string'
                                ? chipItem
                                : chipItem.label
                        }
                        readOnly={true}
                        variant={chipVariant}
                        isMultiLineChip={isMultiLineChip}
                        chipStyles={chipStyles}
                    />
                ))
            )}
            {showTruncateIcon && <img src={TruncateIcon} alt='TRUNCATE_ICON' />}
        </Grid>
    );
};
